/**
 * Color utilities for hex to HSL conversion.
 * Used for dynamic Tailwind CSS variable injection.
 */

export interface HSL {
  h: number;
  s: number;
  l: number;
}

/**
 * Converts a hex color string to HSL components.
 */
export function hexToHSL(hex: string): HSL {
  // Remove hash if present
  hex = hex.replace(/^#/, "");

  // Parse r, g, b
  let r = 0, g = 0, b = 0;
  if (hex.length === 3) {
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  }

  r /= 255;
  g /= 255;
  b /= 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  let h = 0, s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Formats HSL for Tailwind CSS variable.
 * Returns string like "221.2 83.2% 53.3%"
 */
export function formatHSLForTailwind(hsl: HSL): string {
  return `${hsl.h} ${hsl.s}% ${hsl.l}%`;
}

/**
 * Full helper to convert hex to Tailwind-compatible HSL string.
 */
export function hexToTailwindHSL(hex: string): string {
  try {
    const hsl = hexToHSL(hex);
    return formatHSLForTailwind(hsl);
  } catch (e) {
    console.warn(`Invalid hex color: ${hex}. Falling back to default.`);
    return ""; // Empty string will let CSS fallback handle it
  }
}

/**
 * Converts hex to RGB object.
 */
export function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  hex = hex.replace(/^#/, "");
  if (hex.length === 3) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    };
  } else if (hex.length === 6) {
    return {
      r: parseInt(hex.substring(0, 2), 16),
      g: parseInt(hex.substring(2, 4), 16),
      b: parseInt(hex.substring(4, 6), 16),
    };
  }
  return null;
}

/**
 * Calculates relative luminance of a color according to WCAG 2.0.
 */
export function getRelativeLuminance(hex: string): number {
  const rgb = hexToRgb(hex);
  if (!rgb) return 0;

  const a = [rgb.r, rgb.g, rgb.b].map((v) => {
    v /= 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  });

  return a[0] * 0.2126 + a[1] * 0.7152 + a[2] * 0.0722;
}

/**
 * Calculates contrast ratio between two colors according to WCAG 2.0.
 * Returns ratio value from 1.0 to 21.0.
 */
export function getContrastRatio(hex1: string, hex2: string): number {
  try {
    const l1 = getRelativeLuminance(hex1);
    const l2 = getRelativeLuminance(hex2);
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    const ratio = (lighter + 0.05) / (darker + 0.05);
    return Math.round(ratio * 100) / 100;
  } catch (e) {
    console.error("Error calculating contrast ratio:", e);
    return 1.0;
  }
}

export interface ContrastValidationResult {
  ratio: number;
  isNormalTextAA: boolean;
  isLargeTextAA: boolean;
  isNormalTextAAA: boolean;
  isLargeTextAAA: boolean;
}

/**
 * Validates accessibility levels for a foreground/background pair.
 */
export function validateContrast(foreground: string, background: string): ContrastValidationResult {
  const ratio = getContrastRatio(foreground, background);
  return {
    ratio,
    isNormalTextAA: ratio >= 4.5,
    isLargeTextAA: ratio >= 3.0,
    isNormalTextAAA: ratio >= 7.0,
    isLargeTextAAA: ratio >= 4.5,
  };
}

